import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay, isToday, addMonths, subMonths,
} from "date-fns";
import { ChevronLeft, ChevronRight, CalendarDays, Phone, MapPin, Package, CalendarClock, ArrowRight, Truck, Container, ExternalLink, User, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/* ── Per-type style config ── */
const TYPE_CFG = {
  lead:        { dot: "bg-teal-500",   pill: "bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100",     label: "Lead",        legend: "bg-teal-500"   },
  booking:     { dot: "bg-amber-500",  pill: "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100", label: "Confirmed Lead",     legend: "bg-amber-500"  },
  transfer:    { dot: "bg-violet-500", pill: "bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100", label: "Consignment", legend: "bg-violet-500" },
  consignment: { dot: "bg-indigo-500", pill: "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100", label: "Trip Sheet", legend: "bg-indigo-500" },
};

/* ── Tooltip shown on hover ── */
function EventTooltip({ ev, side }) {
  const cfg = TYPE_CFG[ev.type];
  return (
    <div
      className={`
        absolute z-50 w-60 bg-white border border-[#E2E8F0] shadow-xl rounded-sm p-3 space-y-2
        pointer-events-none
        ${side === "right" ? "right-full mr-2 top-0" : "left-full ml-2 top-0"}
      `}
    >
      {/* Type badge */}
      <div className="flex items-center gap-1.5">
        <span className={`w-2 h-2 rounded-full shrink-0 ${cfg.dot}`} />
        <span className={`text-[10px] font-bold uppercase tracking-widest ${
          ev.type === "lead"        ? "text-teal-700"   :
          ev.type === "booking"     ? "text-amber-700"  :
          ev.type === "consignment" ? "text-indigo-700" :
                                      "text-violet-700"
        }`}>
          {cfg.label}
        </span>
      </div>

      {/* Customer / entity name */}
      <p className="text-sm font-bold text-[#0F172A] leading-tight">{ev.title}</p>

      {/* Details */}
      <div className="space-y-1.5 border-t border-[#F1F5F9] pt-2">

        {/* Date row — all types */}
        <div className="flex items-center gap-1.5 text-xs text-[#475569]">
          <CalendarClock size={11} className="shrink-0 text-[#94A3B8]" />
          <span>{format(new Date(ev.date + "T00:00:00"), "dd MMM yyyy")}</span>
        </div>

        {/* ── Lead ── */}
        {ev.type === "lead" && (
          <>
            {ev.phone && (
              <div className="flex items-center gap-1.5 text-xs text-[#475569]">
                <Phone size={11} className="shrink-0 text-[#94A3B8]" />
                <span>{ev.phone}</span>
              </div>
            )}
            {ev.action && ev.action !== "Follow-up" && (
              <div className="flex items-start gap-1.5 text-xs text-[#475569]">
                <span className="shrink-0 mt-0.5 w-2.5 h-2.5 rounded-full border border-[#94A3B8]" />
                <span className="leading-tight">{ev.action}</span>
              </div>
            )}
            {ev.moving_from && ev.moving_to && (
              <div className="flex items-center gap-1 text-xs text-[#475569]">
                <MapPin size={11} className="shrink-0 text-[#94A3B8]" />
                <span>{ev.moving_from}</span>
                <ArrowRight size={10} className="shrink-0 text-[#CBD5E1]" />
                <span>{ev.moving_to}</span>
              </div>
            )}
          </>
        )}

        {/* ── Booking ── */}
        {ev.type === "booking" && (
          <>
            {ev.moving_from && ev.moving_to && (
              <div className="flex items-center gap-1 text-xs text-[#475569]">
                <MapPin size={11} className="shrink-0 text-[#94A3B8]" />
                <span>{ev.moving_from}</span>
                <ArrowRight size={10} className="shrink-0 text-[#CBD5E1]" />
                <span>{ev.moving_to}</span>
              </div>
            )}
            {ev.load_size && (
              <div className="flex items-center gap-1.5 text-xs text-[#475569]">
                <Package size={11} className="shrink-0 text-[#94A3B8]" />
                <span>{ev.load_size}</span>
              </div>
            )}
            {ev.phone && (
              <div className="flex items-center gap-1.5 text-xs text-[#475569]">
                <Phone size={11} className="shrink-0 text-[#94A3B8]" />
                <span>{ev.phone}</span>
              </div>
            )}
          </>
        )}

        {/* ── Transfer ── */}
        {ev.type === "transfer" && (
          <>
            <div className="flex items-center gap-1 text-xs text-[#475569]">
              <MapPin size={11} className="shrink-0 text-[#94A3B8]" />
              <span className="truncate">{ev.origin}</span>
              <ArrowRight size={10} className="shrink-0 text-[#CBD5E1]" />
              <span className="truncate">{ev.destination}</span>
            </div>
            {ev.vehicle && (
              <div className="flex items-center gap-1.5 text-xs text-[#475569]">
                <Truck size={11} className="shrink-0 text-[#94A3B8]" />
                <span>{ev.vehicle}</span>
              </div>
            )}
            {ev.status && (
              <div className="flex items-center gap-1.5 text-xs">
                <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wide ${
                  ev.status === "Completed"  ? "bg-green-50 text-green-700 border border-green-200" :
                  ev.status === "In Transit" ? "bg-blue-50 text-blue-700 border border-blue-200"   :
                                               "bg-violet-50 text-violet-700 border border-violet-200"
                }`}>{ev.status}</span>
              </div>
            )}
          </>
        )}

        {/* ── Consignment ── */}
        {ev.type === "consignment" && (
          <>
            {ev.vehicle && (
              <div className="flex items-center gap-1.5 text-xs text-[#475569]">
                <Truck size={11} className="shrink-0 text-[#94A3B8]" />
                <span>{ev.vehicle}</span>
              </div>
            )}
            <div className="flex items-center gap-1.5 text-xs text-[#475569]">
              <Container size={11} className="shrink-0 text-[#94A3B8]" />
              <span>{ev.transfer_count} transfer{ev.transfer_count !== 1 ? "s" : ""}</span>
            </div>
            {ev.driver && (
              <div className="flex items-center gap-1.5 text-xs text-[#475569]">
                <span className="text-[#94A3B8] font-bold text-[10px]">Driver</span>
                <span>{ev.driver}</span>
              </div>
            )}
            {ev.status && (
              <div className="flex items-center gap-1.5 text-xs">
                <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wide border ${
                  ev.status === "Complete"   ? "bg-green-50 text-green-700 border-green-200"   :
                  ev.status === "In Transit" ? "bg-amber-50 text-amber-700 border-amber-200"   :
                  ev.status === "Confirmed"  ? "bg-indigo-50 text-indigo-700 border-indigo-200":
                  ev.status === "On Hold"    ? "bg-red-50 text-red-600 border-red-200"          :
                                               "bg-blue-50 text-blue-700 border-blue-200"
                }`}>{ev.status}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ── Single event pill with hover tooltip ── */
function EventPill({ ev, colIndex }) {
  const [hovered, setHovered] = useState(false);
  const cfg = TYPE_CFG[ev.type];
  // Show tooltip to the right side for last 2 columns (overflow protection)
  const side = colIndex >= 5 ? "right" : "left";

  return (
    <div
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={`flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-sm truncate cursor-default border ${cfg.pill}`}>
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cfg.dot}`} />
        <span className="truncate">{ev.title}</span>
      </div>
      {hovered && <EventTooltip ev={ev} side={side} />}
    </div>
  );
}

function DayEventCard({ ev, onNavigate }) {
  const cfg = TYPE_CFG[ev.type];
  return (
    <div className="border border-[#E2E8F0] bg-white hover:border-[#1B2A6B] transition-colors">
      <div className={`h-1 ${cfg.dot}`} />
      <div className="p-3">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-[#94A3B8]">{cfg.label}</p>
            <p className="text-sm font-bold text-[#0F172A] mt-0.5">{ev.title}</p>
          </div>
          {ev.status && (
            <span className="text-[9px] font-bold uppercase bg-[#F1F5F9] text-[#475569] px-1.5 py-0.5 whitespace-nowrap">
              {ev.status}
            </span>
          )}
        </div>

        <div className="space-y-1.5 text-xs text-[#475569]">
          {ev.phone && <div className="flex items-center gap-1.5"><Phone size={11} className="text-[#94A3B8]" /><span>{ev.phone}</span></div>}
          {ev.action && <div className="flex items-start gap-1.5"><CalendarClock size={11} className="text-[#94A3B8] mt-0.5" /><span>{ev.action}</span></div>}
          {ev.moving_from && ev.moving_to && (
            <div className="flex items-center gap-1"><MapPin size={11} className="text-[#94A3B8]" /><span>{ev.moving_from}</span><ArrowRight size={10} /><span>{ev.moving_to}</span></div>
          )}
          {ev.origin && ev.destination && (
            <div className="flex items-center gap-1"><MapPin size={11} className="text-[#94A3B8]" /><span>{ev.origin}</span><ArrowRight size={10} /><span>{ev.destination}</span></div>
          )}
          {ev.vehicle && <div className="flex items-center gap-1.5"><Truck size={11} className="text-[#94A3B8]" /><span>{ev.vehicle}</span></div>}
          {ev.driver && <div className="flex items-center gap-1.5"><User size={11} className="text-[#94A3B8]" /><span>{ev.driver}</span></div>}
          {ev.load_size && <div className="flex items-center gap-1.5"><Package size={11} className="text-[#94A3B8]" /><span>{ev.load_size}</span></div>}
          {ev.type === "consignment" && (
            <div className="flex items-center gap-1.5"><Container size={11} className="text-[#94A3B8]" /><span>{ev.transfer_count} transfer{ev.transfer_count !== 1 ? "s" : ""}</span></div>
          )}
        </div>

        <button
          onClick={onNavigate}
          className="mt-3 w-full text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B] border border-[#1B2A6B] hover:bg-[#1B2A6B] hover:text-white transition-colors py-1.5 flex items-center justify-center gap-1.5"
        >
          <ExternalLink size={10} />
          Open {cfg.label}
        </button>
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selectedDay, setSelectedDay]   = useState(null);
  const { user } = useAuth();
  const isCSM = user?.role === "relationship_manager";

  useEffect(() => {
    const load = async () => {
      try {
        // Build request list, then await all at once to avoid unresolved-promise crash
        const allRequests = isCSM
          ? [axios.get(`${API}/leads`, { withCredentials: true })]
          : [
              axios.get(`${API}/leads`,           { withCredentials: true }),
              axios.get(`${API}/confirmed-leads`, { withCredentials: true }),
              axios.get(`${API}/consignments`,    { withCredentials: true }),
              axios.get(`${API}/trips`,           { withCredentials: true }),
            ];

        const resolved = await Promise.all(allRequests);

        const [leadsRes, bookingsRes, transfersRes, consignmentsRes] = isCSM
          ? [resolved[0], { data: [] }, { data: [] }, { data: [] }]
          : resolved;

        /* Follow-up events from leads */
        const leadEvents = leadsRes.data
          .filter(l => l.next_followup_date && l.status !== "Converted")
          .map(l => ({
            type:        "lead",
            date:        l.next_followup_date,
            id:          l.id,
            title:       l.customer_name,
            phone:       l.phone,
            action:      l.next_followup_action || "Follow-up",
            moving_from: l.moving_from,
            moving_to:   l.moving_to,
          }));

        /* Shifting date events from bookings */
        const bookingEvents = bookingsRes.data
          .filter(b => b.shifting_date)
          .map(b => ({
            type:        "booking",
            date:        b.shifting_date,
            id:          b.id,
            title:       b.customer_name,
            phone:       b.phone,
            moving_from: b.moving_from,
            moving_to:   b.moving_to,
            load_size:   b.load_size,
          }));

        /* Transfer date events */
        const transferEvents = transfersRes.data
          .filter(t => t.transfer_date)
          .map(t => ({
            type:        "transfer",
            date:        t.transfer_date,
            id:          t.id,
            title:       t.customer_name || `${t.origin_label} → ${t.destination_label}`,
            origin:      t.origin_label,
            destination: t.destination_label,
            vehicle:     t.vehicle_display,
            status:      t.status,
          }));

        /* Consignment date events */
        const consignmentEvents = consignmentsRes.data
          .filter(c => c.consignment_date)
          .map(c => ({
            type:           "consignment",
            date:           c.consignment_date,
            id:             c.id,
            title:          c.consignment_number,
            vehicle:        c.vehicle_display || c.temp_vehicle_number,
            status:         c.status,
            transfer_count: c.transfer_count || 0,
            driver:         c.driver_name || null,
          }));

        setEvents([...leadEvents, ...bookingEvents, ...transferEvents, ...consignmentEvents]);
      } catch {
        toast.error("Failed to load calendar.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isCSM]);

  /* ── Calendar grid ── */
  const monthStart = startOfMonth(currentMonth);
  const monthEnd   = endOfMonth(currentMonth);
  const gridStart  = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd    = endOfWeek(monthEnd,     { weekStartsOn: 1 });
  const days       = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const getEvents = (day) => {
    const ds = format(day, "yyyy-MM-dd");
    return events.filter(e => e.date === ds);
  };

  const selectedEvents = selectedDay ? getEvents(selectedDay) : [];
  const eventPath = (ev) => ({
    lead: `/leads/${ev.id}`,
    booking: `/confirmed-leads/${ev.id}`,
    transfer: `/consignments/${ev.id}`,
    consignment: `/trips/${ev.id}`,
  })[ev.type];

  return (
    <div className="space-y-5" data-testid="calendar-page">

      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#1B2A6B]">Calendar</h2>
          <p className="text-xs text-[#475569] mt-0.5">Follow-ups, shifting dates, consignments and trip sheets</p>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-5 text-xs">
          {Object.entries(TYPE_CFG)
            .filter(([key]) => isCSM ? key === "lead" : true)
            .map(([key, cfg]) => (
              <div key={key} className="flex items-center gap-1.5">
                <span className={`w-3 h-3 rounded-sm shrink-0 ${cfg.legend}`} />
                <span className="text-[#475569] font-semibold">{cfg.label}</span>
              </div>
            ))}
        </div>
      </div>

      <div className="flex bg-white border border-[#E2E8F0]">
        {/* Calendar card */}
        <div className="flex-1 min-w-0">

        {/* Month nav bar */}
        <div className="flex items-center justify-between px-6 py-4 bg-[#1B2A6B] border-b-4 border-[#FFD700]">
          <button
            onClick={() => setCurrentMonth(m => subMonths(m, 1))}
            data-testid="cal-prev"
            className="p-1.5 rounded-sm text-white/60 hover:text-white hover:bg-white/10 transition-colors"
          >
            <ChevronLeft size={18} />
          </button>

          <div className="flex items-center gap-2.5">
            <CalendarDays size={16} className="text-[#FFD700]" />
            <h3 className="text-base font-bold text-white tracking-wide" data-testid="cal-month-label">
              {format(currentMonth, "MMMM yyyy")}
            </h3>
          </div>

          <button
            onClick={() => setCurrentMonth(m => addMonths(m, 1))}
            data-testid="cal-next"
            className="p-1.5 rounded-sm text-white/60 hover:text-white hover:bg-white/10 transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Day name headers */}
        <div className="grid grid-cols-7 border-b border-[#E2E8F0]">
          {DAY_HEADERS.map(d => (
            <div key={d} className="py-2.5 text-center text-[10px] font-bold uppercase tracking-widest text-[#475569] bg-[#F9FAFB]">
              {d}
            </div>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-sm text-[#94A3B8]">Loading...</div>
        ) : (
          <div className="grid grid-cols-7">
            {days.map((day, idx) => {
              const dayEvents = getEvents(day);
              const inMonth   = isSameMonth(day, currentMonth);
              const todayCell = isToday(day);
              const selectedCell = selectedDay && isSameDay(day, selectedDay);
              const isLastCol = (idx + 1) % 7 === 0;
              const colIndex  = idx % 7;

              return (
                <div
                  key={idx}
                  role="button"
                  tabIndex={0}
                  data-testid={`cal-day-${format(day, "yyyy-MM-dd")}`}
                  onClick={() => setSelectedDay(selectedCell ? null : day)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelectedDay(selectedCell ? null : day);
                    }
                  }}
                  className={`min-h-[108px] border-b border-[#E2E8F0] p-2 cursor-pointer transition-colors hover:bg-[#F0F4FF] ${
                    isLastCol ? "" : "border-r"
                  } ${selectedCell ? "bg-[#EEF2FF] ring-2 ring-inset ring-[#1B2A6B]" : inMonth ? "bg-white" : "bg-[#F9FAFB]"}`}
                >
                  {/* Date number */}
                  <div className="flex justify-start mb-1.5">
                    <span className={`w-7 h-7 flex items-center justify-center text-xs font-bold rounded-full ${
                      todayCell
                        ? "bg-[#1B2A6B] text-white"
                        : inMonth
                          ? "text-[#0F172A]"
                          : "text-[#CBD5E1]"
                    }`}>
                      {format(day, "d")}
                    </span>
                  </div>

                  {/* Events */}
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 3).map((ev, i) => (
                      <EventPill key={i} ev={ev} colIndex={colIndex} />
                    ))}
                    {dayEvents.length > 3 && (
                      <p className="text-[10px] text-[#94A3B8] font-medium pl-1">
                        +{dayEvents.length - 3} more
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>

        {/* Selected-day event panel */}
        <aside className="w-[380px] shrink-0 border-l border-[#E2E8F0] bg-white flex flex-col overflow-hidden">
          {!selectedDay ? (
            <div className="min-h-[520px] flex flex-col items-center justify-center text-center px-8 gap-3">
              <CalendarDays size={40} className="text-[#E2E8F0]" />
              <p className="text-sm font-semibold text-[#475569]">Click a day to see all events</p>
              <p className="text-xs text-[#94A3B8]">Every event on the selected date will appear here</p>
            </div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-[#E2E8F0] bg-[#F9FAFB] flex items-start justify-between shrink-0">
                <div>
                  <p className="text-sm font-black text-[#0F172A]">{format(selectedDay, "EEEE, d MMMM yyyy")}</p>
                  <p className="text-[10px] text-[#94A3B8] mt-0.5">
                    {selectedEvents.length === 0
                      ? "No events scheduled"
                      : `${selectedEvents.length} event${selectedEvents.length !== 1 ? "s" : ""}`}
                  </p>
                </div>
                <button onClick={() => setSelectedDay(null)} aria-label="Close day details" className="text-[#94A3B8] hover:text-[#475569] p-1">
                  <X size={15} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {selectedEvents.length === 0 ? (
                  <p className="text-sm text-center text-[#94A3B8] py-10">No events on this day</p>
                ) : selectedEvents.map((ev, index) => (
                  <DayEventCard key={`${ev.type}-${ev.id}-${index}`} ev={ev} onNavigate={() => navigate(eventPath(ev))} />
                ))}
              </div>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
